// The live sales demo's switch, its gate and its id prefix, with nothing else: this file is imported
// synchronously by the hooks outside src/liveDemo/ (the REST choke point, the uploader, the telemetry
// senders), so it stays tiny. The demo itself (live.js, catalog.js) is loaded on demand.
//
// The code ships in every build; WHO can use it is decided at runtime, by the restaurant's client:
//   - Settings shows «Modo demo» only when liveDemoAllowed(restaurant) (SettingsScreen.vue);
//   - boot clears a flag that is 'on' where it is not allowed (enforceLiveDemoGate, App.vue), so a hook
//     reading liveDemoOn() before the restaurant is known can only ever see an 'on' that a Bronze device wrote;
//   - public/index.html paints the design system's service screen when the flag is 'on' (see setLiveDemo).

/** Bronze's own client id (restaurant.client): the only one that can run the demo. */
export const LIVE_DEMO_CLIENT = 'BRONZE';

export const LIVE_DEMO_KEY = 'bronzeLiveDemo';
export const LIVE_DEMO_CATEGORY_KEY = 'bronzeLiveDemoCategory';

/** Every id of a demo order (order, raw row, analysis, ticket line) starts with this. */
export const DEMO_ID_PREFIX = 'demo-live-';

/** True when this restaurant (the one fetchRestaurantDoc returns) belongs to Bronze's own client. */
export function liveDemoAllowed(restaurant) {
    return Boolean(restaurant) && restaurant.client === LIVE_DEMO_CLIENT;
}

/** 'on' | 'off' | null (absent, or anything else). */
export function liveDemoMode() {
    try {
        const value = window.localStorage.getItem(LIVE_DEMO_KEY);
        return value === 'on' || value === 'off' ? value : null;
    } catch (e) {
        return null;
    }
}

/** True while the presenter has «Modo demo» switched on. */
export function liveDemoOn() {
    return liveDemoMode() === 'on';
}

/** The id of the category the demo was started with (catalog.js validates it), or null. */
export function liveDemoCategory() {
    try {
        return window.localStorage.getItem(LIVE_DEMO_CATEGORY_KEY) || null;
    } catch (e) {
        return null;
    }
}

/**
 * Writes the switch (Settings reloads the app right after; boot reads it). Turning it on stores the category
 * the presenter chose. It does not touch the service-screen lane: public/index.html reads this very flag and
 * paints the design system's, on every host, while it is 'on'. Throws when the storage is blocked.
 */
export function setLiveDemo(on, category) {
    const storage = window.localStorage;
    if (on) {
        if (category) storage.setItem(LIVE_DEMO_CATEGORY_KEY, category);
        storage.setItem(LIVE_DEMO_KEY, 'on');
        return;
    }
    storage.setItem(LIVE_DEMO_KEY, 'off');
}

/**
 * Boot check (App.vue.handleLoginSuccess, once the restaurant is known): a flag that is 'on' where the demo is
 * not allowed (the tablet moved to another client, stale storage) is turned off. Returns true when it did, and
 * only when the flag now reads off, so the caller reloads once and never in a loop: a storage that refuses
 * the write leaves the flag on, and then nothing reloads.
 */
export function enforceLiveDemoGate(restaurant) {
    if (!liveDemoOn() || liveDemoAllowed(restaurant)) return false;
    try {
        setLiveDemo(false);
    } catch (e) {
        return false;
    }
    return !liveDemoOn();
}

/** True for the id of a demo order (or of anything that belongs to one). */
export function isLiveDemoId(id) {
    return typeof id === 'string' && id.startsWith(DEMO_ID_PREFIX);
}

/** True when a request (path, body, variables...) names a demo id anywhere. */
export function mentionsLiveDemo(value) {
    try {
        return JSON.stringify(value).includes(DEMO_ID_PREFIX);
    } catch (e) {
        return false;
    }
}
